/**
 * adminUsersApi.ts — typed client for the user-management admin API (T-411, Slice 1).
 *
 * Mirrors adminApi.ts exactly: authedFetch attaches the Cognito token, every helper throws on a
 * non-2xx, and the server's message is carried through verbatim — those messages are written for
 * an operator ("use Resend on their row instead"), and swallowing them into "request failed"
 * throws away the only actionable part.
 *
 * Note the 207 on invite: partial success (user created, role not assigned) is `res.ok`, so the
 * CALLER must check `roleAssigned` — a 207 treated as a plain success would tell an admin the
 * role was set when it was not.
 */
import { authedFetch } from './api'

const BASE = '/api/admin/users'

export type UserState = 'disabled' | 'active' | 'invited' | 'invite_expired' | 'unknown'
export type InviteClockBasis = 'recorded_send' | 'account_created' | 'not_applicable'

export interface AdminUserRow {
  username: string
  email: string | null
  state: UserState
  rawStatus: string | null
  enabled: boolean
  isAdmin: boolean
  plan: string | null
  createdAt: string | null
  inviteSentAt: string | null
  inviteClockBasis: InviteClockBasis
}

export interface AdminUsersMeta {
  mailer: string
  dailyInviteCap: number
  invitesSentToday: number
  inviteValidityDays: number
}

export interface AdminUsersResponse {
  users: AdminUserRow[]
  nextToken: string | null
  meta: AdminUsersMeta
}

export interface InviteResult {
  invited: boolean
  roleAssigned: boolean
  username: string
  inviteValidityDays?: number
  error?: string
}

async function unwrap<T>(res: Response, what: string): Promise<T> {
  if (!res.ok) {
    let message = `${what} failed: ${res.status}`
    try {
      const body = (await res.json()) as { error?: string }
      if (body?.error) message = body.error
    } catch {
      /* non-JSON error body — keep the status message */
    }
    throw new Error(message)
  }
  return res.json() as Promise<T>
}

// A bodyless POST must NOT declare a JSON content-type — Fastify 400s on
// "content-type is application/json but the body is empty" (documented in adminApi.ts,
// found in live UAT because server.inject never reproduces it).
const jsonInit = (method: string, body?: unknown): RequestInit =>
  body === undefined
    ? { method }
    : { method, headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }

export async function fetchUsers(): Promise<AdminUsersResponse> {
  return unwrap(await authedFetch(BASE), 'Loading users')
}

export async function inviteUser(input: {
  email: string
  makeAdmin?: boolean
  note?: string
}): Promise<InviteResult> {
  return unwrap(await authedFetch(BASE, jsonInit('POST', input)), 'Invite')
}

export async function resendInvite(username: string): Promise<{ resent: boolean }> {
  return unwrap(
    await authedFetch(`${BASE}/${encodeURIComponent(username)}/resend`, jsonInit('POST')),
    'Resend',
  )
}

export async function setAdminRole(
  username: string,
  action: 'add' | 'remove',
): Promise<{ isAdmin: boolean; notice: string }> {
  return unwrap(
    await authedFetch(
      `${BASE}/${encodeURIComponent(username)}/roles`,
      jsonInit('POST', { role: 'admins', action }),
    ),
    'Role change',
  )
}

/** Display label + tone for a derived state. Tone drives the badge colour token. */
export function stateLabel(row: AdminUserRow): { label: string; tone: 'good' | 'bad' | 'neutral' } {
  switch (row.state) {
    case 'active':
      return { label: 'Active', tone: 'good' }
    case 'invited':
      return { label: 'Invited', tone: 'neutral' }
    case 'invite_expired':
      return { label: 'Invite expired', tone: 'bad' }
    case 'disabled':
      return { label: 'Disabled', tone: 'bad' }
    default:
      // Show Cognito's own word rather than inventing a friendly one for a state we have not
      // designed for — an undesigned state should look undesigned.
      return { label: row.rawStatus ?? 'Unknown', tone: 'neutral' }
  }
}
